import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Store, Trash2, Eye, EyeOff, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Marketplace() {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ route_origin: "", route_destination: "", available_date: "", available_capacity: 1, notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/marketplace/listings"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.route_origin || !form.route_destination) { toast.error("Inserisci origine e destinazione"); return; }
    try {
      const payload = { ...form, available_capacity: Number(form.available_capacity) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/marketplace/listings", payload); toast.success("Annuncio creato");
      setForm({ route_origin: "", route_destination: "", available_date: "", available_capacity: 1, notes: "" });
      setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patch = async (id, body) => {
    try { await api.patch(`/marketplace/listings/${id}`, body); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/marketplace/listings/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Store size={24} className="text-blue-700" /> Marketplace</h1>
          <p className="text-sm text-slate-500 mt-1">Capacità e tratte disponibili pubblicate dalle aziende partner</p>
        </div>
        <Button onClick={() => setAdding(!adding)} className="bg-blue-900 hover:bg-blue-800" data-testid="listing-add-btn"><Plus size={16} className="mr-1.5" /> Nuovo annuncio</Button>
      </div>

      {adding && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-3 max-w-2xl" data-testid="listing-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Origine *</Label><Input value={form.route_origin} onChange={(e) => setForm({ ...form, route_origin: e.target.value })} data-testid="listing-origin" /></div>
            <div className="space-y-1.5"><Label>Destinazione *</Label><Input value={form.route_destination} onChange={(e) => setForm({ ...form, route_destination: e.target.value })} data-testid="listing-destination" /></div>
            <div className="space-y-1.5"><Label>Data disponibile</Label><Input type="date" value={form.available_date} onChange={(e) => setForm({ ...form, available_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Capacità (feretri)</Label><Input type="number" min="1" value={form.available_capacity} onChange={(e) => setForm({ ...form, available_capacity: e.target.value })} data-testid="listing-capacity" /></div>
          </div>
          <div className="flex gap-2"><Button onClick={create} className="bg-blue-900 hover:bg-blue-800" data-testid="listing-save-btn">Pubblica come bozza</Button><Button variant="ghost" onClick={() => setAdding(false)}>Annulla</Button></div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-8">Nessun annuncio.</p>}
        {items.map((l) => (
          <div key={l.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="listing-card">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg font-semibold capitalize">{l.route_origin} → {l.route_destination}</div>
                <div className="text-xs text-slate-400 mt-1">Capacità {l.available_capacity} · {l.available_date ? formatDate(l.available_date) : "data flessibile"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "published" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{l.status}</span>
                {l.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">visibile</span>}
              </div>
            </div>
            {l.notes && <p className="text-sm text-slate-500 mt-2">{l.notes}</p>}
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => patch(l.id, { status: l.status === "published" ? "draft" : "published" })} data-testid="listing-publish-btn">
                <Send size={13} className="mr-1" /> {l.status === "published" ? "Ritira" : "Pubblica"}
              </Button>
              {isStaff && (
                <Button size="sm" variant="outline" onClick={() => patch(l.id, { visible: !l.visible })} data-testid="listing-visibility-btn">
                  {l.visible ? <><EyeOff size={13} className="mr-1" /> Nascondi</> : <><Eye size={13} className="mr-1" /> Mostra</>}
                </Button>
              )}
              <button onClick={() => del(l.id)} className="text-slate-400 hover:text-rose-600 p-1.5 ml-auto" data-testid="listing-delete-btn"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
